import * as THREE from 'three'
import fixMaterials from './fixMaterials'

export default function normalizeProduct(product: THREE.Object3D): THREE.Object3D {
  product.updateWorldMatrix(true, true)
  // Note: Adding a zero vector has no effect on the position.
  // Centre the product on the world Y axis.
  //
  // Everything downstream assumes it is already there: updateLightPosition counter-rotates
  // the SCENE about the world origin to give the turntable feel, zoomToObject's cleanup
  // parks the orbit target at (0, centre.y, 0), and controlUpdate kills any horizontal
  // focal offset to keep it pinned. A GLB whose author left the model off-origin breaks
  // all three at once: the counter-rotation becomes an orbit of that radius, so the
  // product sweeps across the screen as the camera turns and can leave it entirely, while
  // appearing to spin far faster than the drag.
  //
  // Only X and Z. Y carries meaning — bottomY and geometryCenter.y place cylindrical
  // sections, and zoomToObject frames on centre.y — and is untouched.
  //
  // Moving the ROOT, not the geometry, so root-local coordinates are unchanged: a section
  // placement authored against this product stays valid, and the admin portal and the
  // Customizer agree whether or not each of them applies this.
  //
  // A product already centred shifts by ~0, so nothing in the existing catalogue moves.
  const bounds = new THREE.Box3().setFromObject(product)
  if (!bounds.isEmpty()) {
    const centre = bounds.getCenter(new THREE.Vector3())
    product.position.x -= centre.x
    product.position.z -= centre.z
    product.updateWorldMatrix(true, true)
  }

  const renderer: THREE.WebGLRenderer = (window as any).threeViewer.renderer
  const transformMatrix: THREE.Matrix4 = new THREE.Matrix4()

  // Ensure the color space is set correctly.
  renderer.outputColorSpace = THREE.SRGBColorSpace

  // Assuming fixMaterials is an imported function that processes materials.
  fixMaterials(product, renderer)

  product.traverse((child: THREE.Object3D) => {
    /*if (child instanceof THREE.Mesh && child.material) {
            const mats = Array.isArray(child.material) ? child.material : [child.material]

            mats.forEach(m => {
                console.log((child.material as any).name, m.envMapIntensity)
                if ('envMapIntensity' in m) m.envMapIntensity = 3
            })
        }*/

    // Use a type guard to ensure the child is a Mesh with geometry.
    if (child instanceof THREE.Mesh && child.geometry) {
      const mats = Array.isArray(child.material) ? child.material : [child.material]

      mats.forEach((m) => {
        m.side = THREE.DoubleSide
        m.needsUpdate = true
      })

      child.updateWorldMatrix(true, false)

      // Ensure bounding box is computed before use.
      child.geometry.computeBoundingBox()

      // Safely access the bounding box after computation.
      const boundingBox = child.geometry.boundingBox as THREE.Box3

      const localCenter = new THREE.Vector3()
      boundingBox.getCenter(localCenter)

      const worldCenter: THREE.Vector3 = child.localToWorld(localCenter.clone())

      const mm: THREE.Vector3 = worldCenter.clone().multiplyScalar(1000)

      const bottomY: number = new THREE.Box3().setFromObject(child).min.y

      // Store custom data.
      child.userData.geometryCenter = mm
      child.userData.bottomY = bottomY

      child.updateMatrix()

      // Apply the child's local matrix directly to the geometry vertices.
      transformMatrix.copy(child.matrix)
      child.geometry.applyMatrix4(transformMatrix)

      // Reset the child's local transformation, as it's now baked into the geometry.
      child.position.set(0, 0, 0)
      child.rotation.set(0, 0, 0)
      child.scale.set(1, 1, 1)

      // Update matrices to reflect the reset transformation.
      child.updateMatrix()
      child.updateMatrixWorld(true)
    }
  })

  // Return the modified product as requested.
  return product
}
